import $ from 'jquery';
import moment from 'moment-timezone';
import Handlebars from 'handlebars';

Handlebars.registerHelper('notEmpty', function (value) {
  return !Handlebars.Utils.isEmpty(value);
});

Handlebars.registerHelper('commas', function (arr, propName) {
  if (Array.isArray(arr)) {
    return typeof propName === 'string' ? arr.map(item => item[propName]).join(', ') : arr.join(', ');
  }
  return arr;
});

Handlebars.registerHelper('ifEquals', function (arg1, arg2, options) {
  return arg1 && arg1 == arg2 ? options.fn(this) : options.inverse(this);
});

export const compileTemplates = (templates) => Object.values(templates).forEach(compileTemplate);

export const compileTemplate = (config) => {
  config.template = Handlebars.compile(config.source)
  return config;
};

export const renderTpl = (config, data) => {
  config.target.innerHTML = config.template(data);
  if ($ && $.fn && $.fn.tooltip) {
    $(config.target).find('[data-toggle="tooltip"]').tooltip();
  }
};

const locale = (typeof pageData !== 'undefined' && pageData.locale) || document.documentElement.getAttribute('lang') || document.body.getAttribute('lang') || 'ja';
// The views hand JS the *display* currency, so a JA page sends `円` where an EN page sends `JPY`
// (see the `currency = …` preamble in app/views/dashboard/show.html.erb). Both mean yen.
const currency = (typeof pageData !== 'undefined' && pageData.currency) || 'JPY';

// "Currency: `¥` + thousands separators, no decimals, no space — `¥149,800`"
// (docs/design-system/readme.md, "Content and voice"). Symbol goes in front, the ISO code never
// trails the amount. Non-yen currencies are not used by the product today; they keep a readable
// fallback rather than a wrong yen sign.
const CURRENCY_SYMBOLS = { JPY: '¥', '円': '¥', '¥': '¥', USD: '$', EUR: '€', GBP: '£' };
const currencySymbol = CURRENCY_SYMBOLS[currency];
// Yen has no minor unit, so it is always written without decimals — regardless of page locale.
const precision = currencySymbol === '¥' ? 0 : 2;
const nf = new Intl.NumberFormat(locale === 'ja' ? 'ja-JP' : 'en-US', {
  minimumFractionDigits: precision,
  maximumFractionDigits: precision,
});

export const formatCurrency = number => {
  const amount = typeof number === 'number' ? number : parseFloat(number);

  if (!isFinite(amount)) {
    return '';
  }

  if (!currencySymbol) {
    return `${nf.format(amount)} ${currency}`;
  }

  // The sign belongs in front of the symbol ("−¥5,000"), never between it and the digits.
  const sign = amount < 0 ? '-' : '';

  return `${sign}${currencySymbol}${nf.format(Math.abs(amount))}`;
};

export const formatDate = date => {
  if (typeof date === 'object' && date.constructor.name === 'Date') {
    return date.toLocaleString(locale);
  }
  return date;
};

export const formatStatus = status => typeof status === 'string' ? status.split('_').join(' ') : '';

// File sizes for the media-kit rows. Bytes and kilobytes are written whole, megabytes and above with
// one decimal — the same shape the OS file dialogs use, so the number reads as a size at a glance.
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

export const formatFileSize = bytes => {
  const size = typeof bytes === 'number' ? bytes : parseInt(bytes, 10);

  if (!isFinite(size) || size <= 0) {
    return '';
  }

  let unit = 0;
  while (size >= Math.pow(1024, unit + 1) && unit < FILE_SIZE_UNITS.length - 1) {
    unit += 1;
  }

  const value = size / Math.pow(1024, unit);
  const rounded = unit < 2 ? Math.round(value) : Math.round(value * 10) / 10;

  return `${rounded} ${FILE_SIZE_UNITS[unit]}`;
};

export const uniqueListener = (elem, event, callback) => {
  elem.removeEventListener(event, callback);
  elem.addEventListener(event, callback);
};

export const locationOrigin = () => {
  return window.location.protocol + "//"
    + window.location.hostname
    + (window.location.port ? ':' + window.location.port : '');
};

export const DTFormat = 'YYYY.MM.DD HH:mm';
// The long form is what the checkout summary shows the customer before they pay, so it mirrors the
// server's `long_date_with_time` shape byte-for-byte in both locales — `Jun 30, 2026 · 09:00` in EN,
// `2026年6月30日 · 09:00` in JA (the zone is appended by `momentToRemoteLong`).
export const DTFormatLong = locale === 'ja' ? 'YYYY年M月D日 · HH:mm' : 'MMM D, YYYY · HH:mm';
export const DTFormatForPicker = 'Y.m.d H:i';
const timezone = (typeof pageData !== 'undefined' && pageData.user?.timezone) || 'Japan';

// Dates the user only *reads* — table cells, detail rows — are one line, no time, and locale-aware:
// `Jul 28, 2026` in EN, `2026年8月5日` in JA (docs/design-system/readme.md, "Content and voice").
// `DTFormat` above stays as it is: the datetimepicker round-trips through it and needs Y.m.d H:i.
export const DTFormatDisplayDate = locale === 'ja' ? 'YYYY年M月D日' : 'MMM D, YYYY';

// An em dash — never a hyphen — is what an empty value looks like across the design system.
export const EMPTY_VALUE = '—';

export const formatDisplayDate = (value, tz = timezone) => {
  if (!value) {
    return EMPTY_VALUE;
  }

  const parsed = tz ? moment(value).tz(tz) : moment(value);

  return parsed.isValid() ? parsed.format(DTFormatDisplayDate) : EMPTY_VALUE;
};

// `#datetime[name=publish_at]` carries the canonical publication moment as a plain
// `YYYY.MM.DD HH:mm` string, and the xdsoft picker is no longer attached to it — nothing can read
// it back through `datetimepicker('getValue')` any more. Every reader goes through here so the
// four call sites cannot drift apart. The string is wall-clock time in the *user's* timezone: a
// plain `moment(value, DTFormat)` would read it as the browser's and shift the submitted timestamp
// for anyone outside JST.
export const parsePublishAtInput = value => {
  if (!value) {
    return null;
  }

  const parsed = moment.tz(String(value).trim(), DTFormat, timezone);

  return parsed.isValid() ? parsed : null;
};

export const pickerToMoment = input => {
  if (moment.isDate(input)) {
    const pickerTime = moment(input).format(DTFormat);
    return moment.tz(pickerTime, DTFormat, timezone);
  } else {
    return input;
  }
};
export const momentToAPI = input => {
  if (moment.isMoment(input)) {
    return input.toISOString();
  } else {
    return input;
  }
};
export const momentToRemote = input => {
  if (moment.isMoment(input)) {
    return moment(input).format(DTFormat + ' z');
  } else {
    return input;
  }
};
export const momentToRemoteLong = input => {
  if (moment.isMoment(input)) {
    return moment(input).format(DTFormatLong + ' z');
  } else {
    return input;
  }
};
export const momentToLocal = input => {
  if (moment.isMoment(input)) {
    return moment(input).local().format(DTFormat) + ` (${moment.tz.guess()})`;
  } else {
    return input;
  }
};

export const pickerToSelectedTimezone = (input, selectedTimezone) => {
  const momentTime = pickerToMoment(input);
  const dateTimeInZone = momentTime.clone().tz(selectedTimezone);
  return dateTimeInZone.format(DTFormat) + ` <b>(${selectedTimezone})</b>`;
};

export const pickerToLocal = input => momentToLocal(pickerToMoment(input));

export const localePrefix = () => `/${document.body.lang || 'ja'}`;